// src/components/dashboard/GettingStartedCard.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  ChevronRight,
  UserPlus,
  Dumbbell,
  Shield,
  MessageSquare,
  X,
} from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useConnectionStore } from '@/store/connectionStore';
import { useInviteStore } from '@/store/inviteStore';
import { supabase } from '@/lib/supabase';
import { InviteModal } from '@/components/InviteModal';

interface Recommendation {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: string;
  priority: number;
  onClick: () => void;
}

const GettingStartedCard: React.FC = () => {
  const [dismissed, setDismissed] = useState(false);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { profile } = useProfile();
  const { connections } = useConnectionStore();
  const { getRemainingInvites } = useInviteStore();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isInviteLoading, setIsInviteLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const until = localStorage.getItem('recommendations_dismissed_until');
    if (until && Number(until) > Date.now()) {
      setDismissed(true);
    }
  }, []);

  useEffect(() => {
    const loadRecs = async () => {
      if (!profile) return;
      setIsLoading(true);

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        if (userError || !userData) throw userError;

        const { count: activitiesCount } = await supabase
          .from('activities')
          .select('*', { count: 'exact', head: true })
          .or(`player_1_id.eq.${userData.id},player_2_id.eq.${userData.id}`);

        const { count: clubsCount } = await supabase
          .from('user_clubs')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userData.id);

        const { count: postsCount } = await supabase
          .from('forum_topics')
          .select('*', { count: 'exact', head: true })
          .eq('author_id', userData.id);

        const recs: Recommendation[] = [];

        recs.push({
          id: 'connect-players',
          title: 'Connect with Players',
          description:
            connections.length === 0
              ? 'Start building your network by connecting with players in your area'
              : `You have ${connections.length} connections. Expand your network!`,
          icon: <UserPlus className="h-5 w-5 text-gold-400" />,
          action: 'Find Players',
          priority: connections.length === 0 ? 1 : 3,
          onClick: () => navigate('/dashboard/match'),
        });

        if (activitiesCount === 0) {
          recs.push({
            id: 'schedule-activity',
            title: 'Schedule Your First Activity',
            description: 'Find a partner and schedule your first match or training session',
            icon: <Dumbbell className="h-5 w-5 text-gold-400" />,
            action: 'Quick Play',
            priority: 2,
            onClick: () => navigate('/dashboard/quick-play'),
          });
        }

        if (clubsCount === 0) {
          recs.push({
            id: 'join-club',
            title: 'Join a Club',
            description: 'Become a member of exclusive clubs to access premium facilities',
            icon: <Shield className="h-5 w-5 text-gold-400" />,
            action: 'Explore Clubs',
            priority: 2,
            onClick: () => navigate('/dashboard/facilities'),
          });
        }

        recs.push({
          id: 'invite-members',
          title: 'Invite New Members',
          description: 'Expand your network by inviting friends and colleagues',
          icon: <UserPlus className="h-5 w-5 text-gold-400" />,
          action: 'Send Invites',
          priority: 2,
          onClick: () => {
            setIsInviteLoading(true);
            setShowInviteModal(true);
            getRemainingInvites().finally(() => setIsInviteLoading(false));
          },
        });

recs.push({
  id: 'group-activity',
  title: 'Create or Join a Group Activity',
  description: 'Organize a new group activity or join an existing one near you',
  icon: <UserPlus className="h-5 w-5 text-gold-400" />,
  action: 'Groups',
  priority: 2,
  onClick: () =>
    navigate('/dashboard/quick-play', {
      state: { fromNav: true, initialView: 'groups' },
    }),
});


        
        if (postsCount === 0) {
          recs.push({
            id: 'join-forums',
            title: 'Join the Conversation',
            description: 'Participate in forum discussions with other members',
            icon: <MessageSquare className="h-5 w-5 text-gold-400" />,
            action: 'View Forums',
            priority: 4,
            onClick: () => navigate('/dashboard/forums'),
          });
        }

        setRecommendations(
          recs.sort((a, b) => a.priority - b.priority).slice(0, 10)
        );
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecs();
  }, [profile, connections.length, getRemainingInvites, navigate]);

  if (dismissed || isLoading || recommendations.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 relative">
        {/* Dismiss */}
        <button
          onClick={() => {
            const until = Date.now() + 7 * 24 * 60 * 60 * 1000;
            localStorage.setItem('recommendations_dismissed_until', String(until));
            setDismissed(true);
          }}
          className="absolute top-3 right-3 p-1.5 bg-dark-300/80 hover:bg-dark-400 rounded-full text-dark-800 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="flex items-center mb-4">
          <Sparkles className="h-6 w-6 text-gold-400 mr-2 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-white">Getting Started</h3>
        </div>

        {/* Cards */}
        <div className="flex overflow-x-auto pb-2 space-x-4 no-scrollbar">
          {recommendations.map((rec) => (
            <div
              key={rec.id}
              className="bg-dark-300 rounded-lg p-4 hover:bg-dark-400 transition-colors cursor-pointer w-[280px] flex-shrink-0"
              onClick={rec.onClick}
            >
              <div className="flex items-start space-x-3">
                <div className="bg-dark-400 p-2 rounded-lg group-hover:bg-dark-500 transition-colors">
                  {rec.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white mb-1 text-base lg:text-sm">
                    {rec.title}
                  </h4>
                  <p className="text-dark-800 text-sm mb-3 line-clamp-2 overflow-hidden">
                    {rec.description}
                  </p>
                  <div className="flex items-center text-gold-400 text-sm font-medium">
                    {rec.action}
                    <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        isLoading={isInviteLoading}
        onClose={() => setShowInviteModal(false)}
      />
    </>
  );
};

export default GettingStartedCard;
